import { defineStore } from 'pinia'
import { AsyncOperationState } from './lib/AsyncOperationState'
import { loadProduct, type Product } from '@/services/product'
import Bugsnag from '@bugsnag/js'
import {
  loadProductReviews,
  type ProductReview,
  type ProductReviewSummary,
} from '@/services/trustpilot'

export type ProductMap = Record<
  string,
  | {
      loadState: AsyncOperationState
      product: Product | null
      reviews: {
        loadState: AsyncOperationState
        payload: {
          reviews: ProductReview[]
          summary: ProductReviewSummary
        } | null
      }
    }
  | undefined
>

export const useProductStore = defineStore('product', {
  state: () => ({
    productsById: {} as ProductMap,
  }),
  actions: {
    async loadProduct(id: string) {
      if (
        this.productsById[id] &&
        this.productsById[id].loadState === AsyncOperationState.DONE
      ) {
        return this.productsById[id]
      }

      this.productsById[id] = {
        loadState: AsyncOperationState.IN_PROGRESS,
        product: null,
        reviews: {
          loadState: AsyncOperationState.IN_PROGRESS,
          payload: null,
        },
      }
      try {
        const res = await loadProduct(id)
        this.productsById[id].product = res.data.product
        this.productsById[id].loadState = AsyncOperationState.DONE

        if (this.productsById[id].product) {
          try {
            const reviewRes = await loadProductReviews(
              ([] as string[])
                .concat(res.data.product.variants.edges.map((v) => v.node.sku))
                .concat(
                  res.data.product.relatedReviews?.value.split(',') ?? [],
                ),
            )
            this.productsById[id].reviews.payload = {
              reviews: reviewRes.reviews,
              summary: reviewRes.summary,
            }
            this.productsById[id].reviews.loadState = AsyncOperationState.DONE
          } catch (e) {
            this.productsById[id].reviews.loadState =
              AsyncOperationState.ERRORED
            Bugsnag.notify(e as Error)
          }
        }
        return this.productsById[id]
      } catch (e) {
        this.productsById[id].loadState = AsyncOperationState.ERRORED
        Bugsnag.notify(e as Error)
      }
    },
  },
  getters: {
    productById: (state) => (id: string) => state.productsById[id],
  },
})
